import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import { NIL } from "uuid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { Icon } from "@/modules/headerFooterFO/Header/components/Icon/Icon"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { PUBLIC_LOCALES } from "@/modules/locales/constants"
import { useLocale } from "@/modules/locales/useLocale"
import { USERS_SIGNIN_PATH } from "@/modules/user/client/constants"

import dropdownMenuStyles from "@/modules/headerFooterFO/Header/styles/DropdownMenu.module.css"

const LoginUser: FunctionComponent = () => {
  const { t } = useTranslation(["module_header_fo"])
  const locale = useLocale()
  const { dataCareerCenter } = useCareerCenterFetch()

  const signinClassNames = clsx(dropdownMenuStyles["hdr-DropdownMenu-submenuLink"], "button-connexion", "signin", {
    "intranet-connexion-link": dataCareerCenter?.intranet,
  })
  const signupClassNames = clsx(dropdownMenuStyles["hdr-DropdownMenu-submenuLink"], "signup", {
    "intranet-connexion-link": dataCareerCenter?.intranet,
  })

  const signinPath = dataCareerCenter?.intranet ? `/?i18n_locale=${locale}` : `/${locale}/${USERS_SIGNIN_PATH}`
  const signupPath = dataCareerCenter?.intranet ? `/?i18n_locale=${locale}` : `/${locale}/profiles/new`
  const isSignupLinkDisplayed = dataCareerCenter?.id !== NIL || PUBLIC_LOCALES.includes(locale)

  return (
    <>
      <button
        className={dropdownMenuStyles["hdr-DropdownMenu-link"]}
        aria-label={t("module_header_fo.dropdown_my_account") as string}
        type="button"
      >
        <span className={dropdownMenuStyles["hdr-DropdownMenu-linkIcon"]}>
          <Icon name="Profile" />
        </span>
        <span
          className={clsx(
            dropdownMenuStyles["hdr-DropdownMenu-linkLabel"],
            dropdownMenuStyles["hdr-DropdownMenu-linkLabel--hidden"]
          )}
        >
          {t("module_header_fo.dropdown_my_account")}
        </span>
        <Icon name="ArrowDown" className={dropdownMenuStyles["hdr-DropdownMenu-arrowIcon"]} />
      </button>

      <ul className={dropdownMenuStyles["hdr-DropdownMenu-submenu"]}>
        <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
          <a className={signinClassNames} href={signinPath} data-testid="signinLink">
            <div className={dropdownMenuStyles["hdr-DropdownMenu-submenuLinkIcon"]}>
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26">
                <g fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.752 16.252l3.576-3.564-.42-2.928h3.336l-.42-2.916 2.928.42L25 6.004l-.42-4.584L19.996 1 9.748 11.248l-3.324-.852L1 15.82l1.872 7.296L10.18 25l5.424-5.424zM23.105 1l-12 12" />
                  <path d="M8.158 21.211l-2.532-.837-.837-2.532 1.685-1.684 3.368 3.368z" />
                </g>
              </svg>
            </div>
            <dl className={dropdownMenuStyles["hdr-DropdownMenu-linkTextWrapper"]}>
              <dt className={clsx(dropdownMenuStyles["hdr-DropdownMenu-linkTitle"])}>
                {t("module_header_fo.dropdown_signin_title")}
              </dt>
              <dd className={dropdownMenuStyles["hdr-DropdownMenu-linkText"]}>
                {t("module_header_fo.dropdown_signin_description")}
              </dd>
            </dl>
          </a>
        </li>

        {isSignupLinkDisplayed && (
          <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
            <a className={signupClassNames} href={signupPath} data-testid="signupLink">
              <div className={dropdownMenuStyles["hdr-DropdownMenu-submenuLinkIcon"]}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                  <g fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23.5 20.75l-4 2.25-4-2.25v-4.5l4-2.25 4 2.25zM19.5 16.5v4M21.5 18.5h-4M5.63 1.81L9 3.5h4.5" />
                    <path d="M13.5 8.25L9 10 4.5 8.25v-6L9 .5l4.5 1.75zM7.5 6v-.5M10.5 6v-.5M15.79 13.52l-.29-1.02L9 11l-6.5 1.5-2 7h13" />
                    <path d="M11.45 11.57L9 15.5l-2.45-3.93" />
                  </g>
                </svg>
              </div>
              <dl className={dropdownMenuStyles["hdr-DropdownMenu-linkTextWrapper"]}>
                <dt className={clsx(dropdownMenuStyles["hdr-DropdownMenu-linkTitle"])}>
                  {t("module_header_fo.dropdown_signup_title")}
                </dt>
                <dd className={dropdownMenuStyles["hdr-DropdownMenu-linkText"]}>
                  {t("module_header_fo.dropdown_signup_description")}
                </dd>
              </dl>
            </a>
          </li>
        )}
      </ul>
    </>
  )
}

export { LoginUser }
